import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Phone, 
  Menu, 
  X, 
  ChevronDown,
  Scale,
  Gavel,
  FileText,
  Users,
  Home as HomeIcon,
  Briefcase,
  FileSignature,
  PhoneCall,
  UserCircle
} from 'lucide-react';

import { navigationData } from '../../data/navigation';
import logo from '../../assets/AKY_Legal_Consultants_Logo.png';
import MobileMenu from './MobileMenu';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  // Helper function to check if a link is active
  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Handle dropdown hover
  const handleMouseEnter = (index) => {
    setOpenDropdown(index);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  // Get icon for each nav item
  const getNavIcon = (name) => {
    switch(name) {
      case 'Home': return <HomeIcon className="w-4 h-4" />;
      case 'Practices': return <Briefcase className="w-4 h-4" />;
      case 'Documentations': return <FileSignature className="w-4 h-4" />;
      case 'About Us': return <Users className="w-4 h-4" />;
      case 'Contact Us': return <PhoneCall className="w-4 h-4" />;
      default: return null;
    }
  };

  // Get icon for dropdown items
  const getDropdownIcon = (name) => {
    if (name.includes('Family')) return <Scale className="w-4 h-4" />;
    if (name.includes('Civil')) return <Gavel className="w-4 h-4" />;
    if (name.includes('Criminal')) return <Scale className="w-4 h-4" />;
    if (name.includes('Firm')) return <Briefcase className="w-4 h-4" />;
    if (name.includes('Leadership')) return <UserCircle className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src={logo} 
              alt="AKY Legal Consultants" 
              className="h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationData.navLinks.map((link, index) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => link.dropdown && handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                {link.dropdown ? (
                  <>
                    <button
                      className={`flex items-center space-x-1 py-2 group ${
                        isActive(link.path)
                          ? 'text-gold font-medium'
                          : 'text-black hover:text-gold transition-colors duration-200 font-medium'
                      }`}
                    >
                      <span className="flex items-center space-x-1">
                        {getNavIcon(link.name)}
                        <span>{link.name}</span>
                      </span>
                      <ChevronDown 
                        className={`w-4 h-4 transition-transform duration-200 ${
                          openDropdown === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    {openDropdown === index && (
                      <div className="absolute left-0 top-full pt-2 w-64 bg-white rounded-lg shadow-xl py-2 border-t-2 border-gold">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center space-x-3 px-4 py-3 ${
                              location.pathname === item.path
                                ? 'text-gold bg-gray-50'
                                : 'text-primary-blue hover:bg-gold hover:text-white'
                            } transition-colors duration-200`}
                          >
                            {getDropdownIcon(item.name)}
                            <span>{item.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`relative py-2 flex items-center space-x-1 ${
                      isActive(link.path, link.exact)
                        ? 'text-gold font-medium'
                        : 'text-black hover:text-gold transition-colors duration-200 font-medium'
                    }`}
                  >
                    {getNavIcon(link.name)}
                    <span>{link.name}</span>
                    {/* Active indicator line */}
                    <span
                      className={`absolute -bottom-1 left-0 h-0.5 bg-gold transition-all duration-300 ${
                        isActive(link.path, link.exact) ? 'w-full' : 'w-0'
                      }`}
                    ></span>
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Phone Number */}
          <a
            href={`tel:${navigationData.phoneLink}`}
            className="hidden lg:flex items-center space-x-2 text-black hover:text-gold transition-colors duration-200 group"
          >
            <Phone className="w-5 h-5 text-gold group-hover:scale-110 transition-transform" />
            <span className="font-medium">{navigationData.phone}</span>
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-primary-blue hover:text-gold transition-colors duration-200"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <MobileMenu 
          navigationData={navigationData}
          isActive={isActive}
          onClose={() => setIsMobileMenuOpen(false)}
          getNavIcon={getNavIcon}
          getDropdownIcon={getDropdownIcon}
        />
      )}
    </header>
  );
};

export default Header;